import { i18nKeys } from './i18n.utils';
import { formatDuration } from './duration.utils';
import { FacilitatorAuditAction, FacilitatorAuditLogEntry } from '../types/JamFacilitatorAuditLog';

/**
 * Best-effort resolvers that turn the raw ids in an audit entry into human-readable values.
 * Each resolver should fall back to the raw id when a lookup misses.
 */
export interface AuditLogResolvers {
  /** Resolve a facilitator/admin vibe id to a display name. */
  actorName: (vibeId: string) => string;
  /** Resolve a target user id (login or vibe id) to a display name. */
  userName: (id: string) => string;
  /** Resolve a challenge id to its title. */
  challengeTitle: (challengeId: string) => string;
  /** Resolve a task id to its task number (as a string). */
  taskNumber: (taskId: string) => string;
}

/**
 * The i18n key + interpolation values for an audit log sentence. The templates embed <b> tags
 * around the team/challenge names, so they are rendered with the react-i18next <Trans> component
 * rather than a plain t() call.
 */
export interface AuditLogSentence {
  i18nKey: string;
  values: Record<string, string>;
}

const MISSING = '--';

const toInt = (value?: string | null): number => {
  if (!value) {
    return 0;
  }
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * Builds the i18n key and interpolation values describing a facilitator audit log entry.
 * Ids are resolved to names/titles/numbers via the provided resolvers, falling back to raw
 * values when a lookup misses. Unknown action types fall back to a generic sentence.
 */
export const composeAuditLogSentence = (
  entry: FacilitatorAuditLogEntry,
  resolvers: AuditLogResolvers
): AuditLogSentence => {
  const { sentences } = i18nKeys.facilitator.actionLog;
  const details = entry.details || {};

  const values: Record<string, string> = {
    actor: resolvers.actorName(entry.actorVibeId),
    team: details.teamName || MISSING,
    challenge: details.challengeId ? resolvers.challengeTitle(details.challengeId) : MISSING,
    task: details.taskId ? resolvers.taskNumber(details.taskId) : MISSING,
    clue: details.clueId || MISSING,
    user: details.targetId ? resolvers.userName(details.targetId) : MISSING,
    points: String(toInt(details.points)),
    time: formatDuration(toInt(details.totalTimeSeconds)),
  };

  switch (entry.action) {
    case FacilitatorAuditAction.ADJUST_TEAM_SCORE:
      return {
        i18nKey: details.challengeId ? sentences.adjustTeamScoreWithChallenge : sentences.adjustTeamScore,
        values,
      };
    case FacilitatorAuditAction.REFUND_CLUE:
      return { i18nKey: sentences.refundClue, values };
    case FacilitatorAuditAction.RESTART_CHALLENGE:
      return { i18nKey: sentences.restartChallenge, values };
    case FacilitatorAuditAction.MARK_CHALLENGE_TASK_COMPLETED:
      return { i18nKey: sentences.markChallengeTaskCompleted, values };
    case FacilitatorAuditAction.ASSIGN_USER_TO_TEAM:
      return { i18nKey: sentences.assignUserToTeam, values };
    case FacilitatorAuditAction.UNASSIGN_USER_FROM_TEAM:
      return { i18nKey: sentences.unassignUserFromTeam, values };
    case FacilitatorAuditAction.ADD_TIME_TO_EVENT:
      return { i18nKey: sentences.addTimeToEvent, values };
    case FacilitatorAuditAction.SUBTRACT_TIME_FROM_EVENT:
      return { i18nKey: sentences.subtractTimeFromEvent, values };
    case FacilitatorAuditAction.LOCK_EVENT_CHALLENGES:
      return { i18nKey: sentences.lockEventChallenges, values };
    case FacilitatorAuditAction.UNLOCK_EVENT_CHALLENGES:
      return { i18nKey: sentences.unlockEventChallenges, values };
    default:
      return { i18nKey: sentences.unknown, values };
  }
};
